import { JUDGE0_LANGUAGE_IDS } from './languageConfig'

const JUDGE0_CONFIG = {
  apiUrl: "https://judge0-ce.p.rapidapi.com",
  apiKey: process.env.REACT_APP_JUDGE0_API_KEY || "",
}

/**
 * Submit code to Judge0 for compilation and execution.
 * Supports C and Java via language parameter.
 */
export async function executeCode(sourceCode, language) {
  if (!JUDGE0_CONFIG.apiKey) {
    throw new Error("Judge0 API key not configured. Set REACT_APP_JUDGE0_API_KEY in your environment.")
  }

  const languageId = JUDGE0_LANGUAGE_IDS[language]
  if (!languageId) {
    throw new Error(`Unsupported language for Judge0: ${language}`)
  }

  const response = await fetch(
    `${JUDGE0_CONFIG.apiUrl}/submissions?base64_encoded=true&wait=true&fields=stdout,stderr,status_id,compile_output,message,time,memory`, 
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-rapidapi-key': JUDGE0_CONFIG.apiKey,
        'x-rapidapi-host': 'judge0-ce.p.rapidapi.com'
      },
      body: JSON.stringify({
        source_code: btoa(unescape(encodeURIComponent(sourceCode))),
        language_id: languageId,
        stdin: "",
        cpu_time_limit: 5,
        memory_limit: 128000 
      })
    }
  )

  if (!response.ok) {
    const errorText = await response.text()
    throw new Error(`Judge0 submission failed: ${response.status} - ${errorText}`)
  }

  const result = await response.json()

  // If still processing, fall back to polling
  if (result.token && result.status_id <= 2) {
    return await pollForResult(result.token)
  }

  return parseResult(result)
}

async function pollForResult(token) {
  let attempts = 0
  const maxAttempts = 15

  while (attempts < maxAttempts) {
    await new Promise(resolve => setTimeout(resolve, 1000))

    const response = await fetch(
      `${JUDGE0_CONFIG.apiUrl}/submissions/${token}?base64_encoded=true&fields=stdout,stderr,status_id,compile_output,message,time,memory`,
      {
        headers: {
          'x-rapidapi-key': JUDGE0_CONFIG.apiKey,
          'x-rapidapi-host': 'judge0-ce.p.rapidapi.com'
        }
      }
    )

    if (!response.ok) {
      throw new Error(`Judge0 polling failed: ${response.status}`)
    }

    const result = await response.json()

    if (result.status_id <= 2) {
      attempts++
      continue
    }

    return parseResult(result)
  }

  throw new Error("Execution timed out — Judge0 took too long to respond.")
}

function decodeBase64(encoded) {
  if (!encoded) return ""
  try {
    const bytes = Uint8Array.from(atob(encoded), c => c.charCodeAt(0))
    return new TextDecoder('utf-8').decode(bytes)
  } catch {
    return encoded
  }
}

/**
 * Parse Judge0 result into a structured object.
 * 
 * Returns: {
 *   success: boolean,
 *   stdout: string,
 *   stderr: string,
 *   compileOutput: string,
 *   statusId: number,
 *   error: { message, type } | null
 * }
 */
function parseResult(result) {
  const stdout = decodeBase64(result.stdout)
  const stderr = decodeBase64(result.stderr)
  const compileOutput = decodeBase64(result.compile_output)

  switch (result.status_id) {
    case 3: // Accepted
      return { success: true, stdout, stderr, compileOutput, statusId: 3, error: null }

    case 6: // Compilation Error
      return {
        success: false, stdout, stderr,
        compileOutput,
        statusId: 6,
        error: {
          message: `Compilation Error:\n${compileOutput}`,
          type: 'CompilationError'
        }
      }

    case 5: // Time Limit Exceeded
      return {
        success: false, stdout, stderr, compileOutput,
        statusId: 5,
        error: {
          message: 'Time Limit Exceeded — your code took too long. Check for infinite loops.',
          type: 'TimeLimitExceeded'
        }
      }

    case 7: // Memory Limit Exceeded
      return {
        success: false, stdout, stderr, compileOutput,
        statusId: 7,
        error: {
          message: 'Memory Limit Exceeded — your code used too much memory.',
          type: 'MemoryLimitExceeded'
        }
      }

    case 11: // Runtime Error (SIGSEGV / NullPointerException / etc.)
      return {
        success: false, stdout, stderr, compileOutput,
        statusId: 11,
        error: {
          message: `Runtime Error:\n${stderr}`,
          type: 'RuntimeError'
        }
      }

    default:
      return {
        success: false, stdout, stderr, compileOutput,
        statusId: result.status_id,
        error: {
          message: stderr || compileOutput || result.message || 'Unknown runtime error',
          type: 'RuntimeError'
        }
      }
  }
}

/**
 * Parse test results from stdout.
 * Expects lines in format: RESULT:1 (pass) or RESULT:0 (fail)
 */
export function parseTestResults(stdout) {
  const lines = stdout.split('\n')
  const results = []

  for (const line of lines) {
    const match = line.trim().match(/^RESULT:(\d)$/)
    if (match) {
      results.push(match[1] === '1')
    }
  }

  return results
}

/**
 * Build a complete compilable source file from session parts.
 * Mirrors the Python pattern: starter + userCode + unit_test
 */
export function buildSource(starterCode, userCode, unitTestCode) {
  return `${starterCode}\n${userCode}\n${unitTestCode}`
}